
import * as React from 'react';
import * as PropTypes from 'prop-types';
import { connectToContext, provideContext } from '../../utilities/components/Context';
import { IOnExecuteProps } from '../commandSet/CommandSet';
import { Omit } from '../../utilities/Omit';
import { isCancel } from '../../utilities/async/CancelError';
import { IEngagementData, ResultType } from '../../facets/LoggerFacets';
import { graft } from '../../utilities/Graft';
import { ITelemetryConsumerStateProps, withTelemetry } from '../telemetry/TelemetryProvider';
import { Engagement, IEngagementSingleSchema } from '@ms/telemetry-manager/lib/events/Engagement.event';
import { Qos, IQosStartSchema } from '@ms/telemetry-manager/lib/events/Qos.event';
import { withDisplayName } from '../../utilities/components/Decorators';

export const COMMAND_CONTEXT_TYPES: PropTypes.ValidationMap<IActionContext> = {
  command: PropTypes.object.isRequired
};

/**
 * @internal
 */
export interface IUpdateActionOptions {
  isAvailable: boolean;
  isToggled: boolean | undefined;
  executingCount: number;
  execute: (onExecuteProps?: IOnExecuteProps) => Promise<void>;
}

/**
 * @internal
 */
export interface IActionContext {
  command: {
    actionKey: string;
    preview?: (options: IUpdateActionOptions, actionKey: string) => void;
    update: (options: IUpdateActionOptions, actionKey: string) => void;
    remove: (actionKey: string) => void;
  };
}

/**
 * @internal
 */
export interface IActionHostProps {
  actionKey: string;
  preview?: (options: IUpdateActionOptions, actionKey: string) => void;
  update: (options: IUpdateActionOptions, actionKey: string) => void;
  remove: (actionKey: string) => void;
}

/**
 * Wrapper for rendering an action which takes callbacks for the state of the action.
 * @internal
 */
export const ActionHost = provideContext(COMMAND_CONTEXT_TYPES, (props: IActionHostProps): IActionContext => {
  return {
    command: {
      actionKey: props.actionKey,
      preview: props.preview,
      update: props.update,
      remove: props.remove
    }
  };
});

/**
 * @public
 */
export interface IActionBaseProps {
  /**
   * A key to assign to this action with respect to an ActionHost.
   */
  actionKey: string;
  /**
   * Whether or not the action should be available.
   * Default is `true`.
   */
  isAvailable?: boolean | undefined;
  /**
   * Whether or not the action should be in a toggled state.
   * Default is `undefined`, which means it does not support toggling.
   */
  isToggled?: boolean | undefined;
  /**
   * A callback which invoked to implement the action's execution behavior.
   * This function must always return a Promise.
   */
  onExecute?: (onExecuteProps: IOnExecuteProps) => Promise<void>;
  /**
   * A callback which fires immediately during React reconciliation when
   * the Action is created or receives new props. This provides an opportunity for the caller
   * to render is next components with the correct state from the Action.
   *
   * This callback will be fored 'out of turn' with respect to React's component flow, and must not
   * be used to cause side-effects by invoking 'setState' within the recipient.
   */
  preview?: (options: IUpdateActionOptions, actionKey: string) => void;
  /**
   * A callback which fires once the Action has finished mounting or updating with new state.
   * Within this callback, it is safe to cause side-effects.
   * Even if a caller handles the 'preview' callback, it must also handle 'update' in order to
   * be able to handle internal updates to Action.
   */
  update: (options: IUpdateActionOptions, actionKey: string) => void;
  /**
   * A callback which fires when the Action is unmounted.
   * It is safe to call setState within this callback.
   */
  remove: (actionKey: string) => void;
}

/**
 * @public
 */
export type IActionProps = Omit<IActionBaseProps, 'actionKey' | 'preview' | 'update' | 'remove'> & {
  /**
   * Data used to fire Engagement events when the Action is invoked.
   */
  engagement?: IEngagementData;
};

interface IActionBaseState {
  executingCount: number;
}

/**
 * @public
 */
export const ActionBase = withDisplayName('ActionBase')(connectToContext(
  COMMAND_CONTEXT_TYPES,
  (context: IActionContext): Pick<IActionBaseProps, 'actionKey' | 'preview' | 'update' | 'remove'> => {
    const {
      command
    } = context;

    return {
      actionKey: command.actionKey,
      preview: command.preview,
      update: command.update,
      remove: command.remove
    };
  })(withDisplayName('ActionBase.Inner')(class extends React.PureComponent<IActionBaseProps, IActionBaseState> {
    private _options!: IUpdateActionOptions;
    /**
     * Redundantly track executing count so that `this._preview` has up-to-date values when invoked.
     */
    private _executingCount: number;

    constructor(props: IActionBaseProps) {
      super(props);

      this.state = {
        executingCount: 0
      };

      this._executingCount = 0;

      this._preview(this.props, this.state);
    }

    public componentWillReceiveProps(nextProps: IActionBaseProps): void {
      // This only gets invoked when the props update, not state.
      // So if the action is executed, `this.state` might not be up-to-date.
      this._preview(nextProps, this.state);
    }

    public componentDidMount(): void {
      // Even if a caller supplies the 'preview' callback, it must still handle the 'update' callback
      // because the caller may only update its internal state once Action has mounted.
      this._update(this.props, this.state);
    }

    public componentDidUpdate(previousProps: IActionBaseProps, previousState: IActionBaseState): void {
      const {
        isToggled
      } = this.props;

      const {
        executingCount
      } = this.state;

      const isAvailable = this._isAvailable(this.props);

      if (isAvailable !== this._isAvailable(previousProps) ||
        isToggled !== previousProps.isToggled ||
        executingCount !== previousState.executingCount) {
        // Even if a caller supplies the 'preview' callback, it must still handle the 'update' callback
        // because the caller may only update its internal state once Action has updated.
        this._update(this.props, this.state);
      }
    }

    public componentWillUnmount(): void {
      this.props.remove(this.props.actionKey);
    }

    public render(): JSX.Element | null {
      return null;
    }

    private _isAvailable(props: IActionBaseProps): boolean {
      const {
        isAvailable = true
      } = props;

      return isAvailable && !!props.onExecute;
    }

    private _execute = async (onExecuteProps: IOnExecuteProps = {}): Promise<void> => {
      const {
        onExecute
      } = this.props;

      if (!onExecute) {
        // Nothing to execute; not available.
        return;
      }

      try {
        this._executingCount = this._executingCount + 1;

        this.setState((previousState: IActionBaseState): IActionBaseState => {
          return {
            executingCount: previousState.executingCount + 1
          };
        });

        await onExecute(onExecuteProps);
      } finally {
        this._executingCount = this._executingCount - 1;

        this.setState((previousState: IActionBaseState): IActionBaseState => {
          return {
            executingCount: previousState.executingCount - 1
          };
        });
      }
    }

    private _preview(props: IActionBaseProps, state: IActionBaseState): void {
      const isAvailable = this._isAvailable(props);

      this._options = graft(this._options || {}, {
        isAvailable: isAvailable,
        isToggled: props.isToggled,
        executingCount: this._executingCount,
        execute: this._execute
      });

      const {
        preview
      } = props;

      if (preview) {
        preview(this._options, props.actionKey);
      }
    }

    private _update(props: IActionBaseProps, state: IActionBaseState): void {
      const {
        isToggled
      } = props;

      const isAvailable = this._isAvailable(props);

      this._options = graft(this._options, {
        isAvailable: isAvailable,
        isToggled: isToggled,
        executingCount: this._executingCount,
        execute: this._execute
      });

      props.update(this._options, props.actionKey);
    }
  })));

// Add prop for engagement logging
const mapProps = (
  telemetryProps: ITelemetryConsumerStateProps,
  props: Pick<IActionBaseProps, 'onExecute'> & Pick<IActionProps, 'engagement'>
): Pick<IActionBaseProps, 'onExecute'> => {
  const {
    telemetry
  } = telemetryProps;

  const {
    onExecute,
    engagement = {
      name: 'Action'
    }
  } = props;

  if (!onExecute) {
    return {};
  }

  return {
    onExecute: async (onExecuteProps: IOnExecuteProps) => {
      const {
        name
      } = engagement;

      telemetry(Engagement).withData((data: IEngagementSingleSchema) => {
        return {
          ...data,
          ...engagement
        };
      }).logData({
        name: name
      });

      const qos = new (telemetry(Qos).withData((data: IQosStartSchema) => {
        return {
          ...data
        };
      }))({ name });

      try {
        await onExecute(onExecuteProps);

        qos.end({
          resultType: ResultType.success
        });
      } catch (e) {
        if (isCancel(e)) {
          qos.end({
            resultType: ResultType.expectedFailure
          });
        } else {
          qos.end({
            resultType: ResultType.failure
          });
        }
      }
    }
  };
};

/**
 * @public
 */
export const Action: React.ComponentType<IActionProps> =
  withDisplayName('Action')(withTelemetry(mapProps)(ActionBase));
